
import * as React from "react"
import { Button } from "@/components/ui/button"
import { Menu } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet"
import quickatooLogo from "@/assets/quickatoo-logo-white.png"

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    setSheetOpen(false); // Close mobile menu
    
    if (!isHomePage) {
      // If not on home page, navigate to home first, then scroll to section
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    
    if (sectionId === 'top') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLearnNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setSheetOpen(false); // Close mobile menu
    navigate('/learn');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-quickatoo-purple">
      <nav className="flex h-16 w-full items-center justify-between px-4 sm:px-8 lg:px-12">
        <div className="flex items-center gap-4">
          <a href="#" onClick={(e) => handleNavigation(e, 'top')} className="flex items-center gap-2">
            <img src={quickatooLogo} alt="Quickatoo" className="h-8 w-auto" />
          </a>
        </div>
        
        <div className="hidden gap-6 md:flex">
          <a href="#" onClick={(e) => handleNavigation(e, 'top')} className="text-sm font-semibold text-white/90 transition-colors hover:text-white">
            Home
          </a>
          <a href="#why-quickatoo" onClick={(e) => handleNavigation(e, 'why-quickatoo')} className="text-sm font-semibold text-white/90 transition-colors hover:text-white">
            Why Quickatoo
          </a>
          <a href="#about" onClick={(e) => handleNavigation(e, 'about')} className="text-sm font-semibold text-white/90 transition-colors hover:text-white">
            About
          </a>
          <a href="#faq" onClick={(e) => handleNavigation(e, 'faq')} className="text-sm font-semibold text-white/90 transition-colors hover:text-white">
            FAQ
          </a>
          <a href="#contact" onClick={(e) => handleNavigation(e, 'contact')} className="text-sm font-semibold text-white/90 transition-colors hover:text-white">
            Contact
          </a>
          <a href="#learn" onClick={handleLearnNavigation} className="text-sm font-semibold text-white/90 transition-colors hover:text-white">
            Learn
          </a>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex md:gap-2">
            <Button variant="outline" size="sm" asChild className="rounded-xl border-white bg-transparent font-semibold text-white hover:bg-white/10 hover:text-white">
              <a href="https://app.quickatoo.com" target="_blank" rel="noopener noreferrer">Sign in</a>
            </Button>
            <Button
              size="sm"
              asChild
              className="rounded-xl bg-quickatoo-yellow font-semibold text-quickatoo-charcoal hover:bg-quickatoo-yellow/90"
            >
              <a href="https://app.quickatoo.com/auth/login?view=sign_up" target="_blank" rel="noopener noreferrer">Sign up</a>
            </Button>
          </div>
          
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="outline" size="icon" className="border-white/70 bg-transparent text-white hover:bg-white/10 hover:text-white">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <div className="flex flex-col gap-4 py-4">
                <a href="#" onClick={(e) => handleNavigation(e, 'top')} className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  Home
                </a>
                <a href="#why-quickatoo" onClick={(e) => handleNavigation(e, 'why-quickatoo')} className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  Why Quickatoo
                </a>
                <a href="#about" onClick={(e) => handleNavigation(e, 'about')} className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  About
                </a>
                <a href="#faq" onClick={(e) => handleNavigation(e, 'faq')} className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  FAQ
                </a>
                <a href="#contact" onClick={(e) => handleNavigation(e, 'contact')} className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  Contact
                </a>
                <a href="#learn" onClick={handleLearnNavigation} className="text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                  Learn
                </a>
                <div className="flex flex-col gap-2 pt-4">
                  <Button variant="outline" asChild>
                    <a href="https://app.quickatoo.com" target="_blank" rel="noopener noreferrer">Sign in</a>
                  </Button>
                  <Button asChild>
                    <a href="https://app.quickatoo.com/auth/login?view=sign_up" target="_blank" rel="noopener noreferrer">Sign up</a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  )
}

export { Navbar }
